import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { RECEIVABLES_PERIOD } from '@/content/payments';
import type { PaymentSummary } from '@/types/payment-reporting';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format-currency';

/** Parte do hero: rótulo com ponto colorido e valor, no mesmo desenho do hero de Relatórios. */
function HeroTile({ label, value, dotClassName }: { label: string; value: number; dotClassName: string }) {
  return (
    <View className="flex-1 gap-1 rounded-2xl bg-white/10 p-3">
      <View className="flex-row items-center gap-1.5">
        <View className={cn('h-1.5 w-1.5 rounded-full', dotClassName)} />
        <ThemedText className="font-medium text-xs text-white/60">{label}</ThemedText>
      </View>
      <ThemedText
        className="font-semibold text-base text-white"
        style={{ fontVariant: ['tabular-nums'] }}
        numberOfLines={1}
        adjustsFontSizeToFit
      >
        {formatCurrency(value)}
      </ThemedText>
    </View>
  );
}

/**
 * Hero escuro do período (mesmo desenho do "Lucro no período" de Relatórios):
 * líquido grande e, dentro, de onde ele vem — bruto e taxas; reembolsos e
 * contestações só quando existem.
 */
export function ReceivablesPeriodHero({
  summary,
  periodLabel,
  fill = false,
}: {
  summary: PaymentSummary;
  periodLabel: string;
  /** Ocupa a altura toda da coluna (web larga, ao lado da grade de saldos). */
  fill?: boolean;
}) {
  const hasLosses = summary.refundedAmount > 0 || summary.disputedAmount > 0;

  return (
    <View
      className={cn('w-full gap-5 rounded-3xl bg-ink p-5', fill && 'flex-1 justify-between')}
      style={{ borderCurve: 'continuous', boxShadow: '0 12px 32px rgba(10, 10, 10, 0.25)' }}
      testID="receivables-period-hero"
    >
      <View className="flex-row items-center justify-between gap-2">
        <ThemedText className="font-medium text-sm text-white/60">
          {RECEIVABLES_PERIOD.heroTitle}
        </ThemedText>
        <View className="rounded-full bg-white/10 px-2.5 py-1">
          <ThemedText className="font-semibold text-xs text-white" numberOfLines={1}>
            {periodLabel}
          </ThemedText>
        </View>
      </View>

      <ThemedText
        className="font-bold text-4xl text-white"
        style={{ fontVariant: ['tabular-nums'] }}
        numberOfLines={1}
        adjustsFontSizeToFit
      >
        {formatCurrency(summary.netAmount)}
      </ThemedText>

      <View className="gap-3">
        <View className="flex-row gap-3">
          <HeroTile
            label={RECEIVABLES_PERIOD.grossLabel}
            value={summary.grossAmount}
            dotClassName="bg-[#4ADE80]"
          />
          <HeroTile
            label={RECEIVABLES_PERIOD.feeLabel}
            value={summary.feeAmount}
            dotClassName="bg-white/40"
          />
        </View>
        {hasLosses ? (
          <View className="flex-row gap-3">
            <HeroTile
              label={RECEIVABLES_PERIOD.refundsLabel}
              value={summary.refundedAmount}
              dotClassName="bg-[#F87171]"
            />
            <HeroTile
              label={RECEIVABLES_PERIOD.disputedLabel}
              value={summary.disputedAmount}
              dotClassName="bg-[#FBBF24]"
            />
          </View>
        ) : null}
      </View>
    </View>
  );
}
